/**
 * Batch Service - API wrapper for batch management
 *
 * Handles all batch-related API calls with proper error handling and type safety
 */

import axios, { AxiosInstance } from 'axios';
import {
  AiProcessing,
  Batch,
  BatchLocation,
  CSVRow,
  BatchFilters,
  CreateBatchRequest,
  CreateBatchResponse,
  BatchProgressResponse,
  PreloadSolarRequest,
  PreloadSolarResponse,
  ProximityAlertsResponse,
  SegmentAssignRequest,
  SegmentAssignResponse,
} from '../types/batch';
import {
  validateAndCorrectDealId,
  checkForMultipleDealIds,
  findDealByRPU,
} from './dealsService';

import { env } from 'env';
// API configuration
const API_URL = env.API_URL || 'http://localhost:5001';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Batch Service
 */
export const batchService = {
  /**
   * Get all batches with optional filters
   */
  getBatches: async (filters: BatchFilters, token: string): Promise<Batch[]> => {
    try {
      const params: any = {};

      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      const response = await apiClient.get('/api/batches', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch batches');
      }

      return response.data.batches || [];
    } catch (error: any) {
      console.error('Error fetching batches:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to fetch batches'
      );
    }
  },

  /**
   * Get single batch details by ID
   */
  getBatchDetails: async (batchId: string, token: string): Promise<Batch> => {
    try {
      const response = await apiClient.get(`/api/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000, // 2 minutes for large batches with many locations
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch batch details');
      }

      return response.data.batch;
    } catch (error: any) {
      console.error('Error fetching batch details:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to fetch batch details'
      );
    }
  },

  /**
   * (Re)lanza la sesión Devin que genera borradores IA para el batch (admin).
   * POST /api/batches/:id/ai-process — body {force} para reemplazar una sesión activa.
   * Devuelve {triggered:false, error} en vez de lanzar cuando el backend responde 409/502/503.
   */
  triggerAiProcessing: async (
    batchId: string,
    token: string,
    force = false
  ): Promise<{ triggered: boolean; error?: string; ai_processing?: AiProcessing | null }> => {
    try {
      const response = await apiClient.post(
        `/api/batches/${batchId}/ai-process`,
        { force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return {
        triggered: !!response.data.success,
        error: response.data.error,
        ai_processing: response.data.ai_processing ?? null,
      };
    } catch (error: any) {
      const data = error.response?.data;
      if (data && typeof data === 'object' && ('error' in data || 'ai_processing' in data)) {
        return { triggered: false, error: data.error, ai_processing: data.ai_processing ?? null };
      }
      console.error('Error triggering AI processing:', error);
      throw new Error(error.message || 'Failed to trigger AI processing');
    }
  },

  /**
   * Create new batch from CSV data
   */
  createBatch: async (
    request: CreateBatchRequest,
    token: string
  ): Promise<CreateBatchResponse> => {
    try {
      const response = await apiClient.post('/api/batches', request, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000, // 5 minutes for large batch creation (200+ locations)
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create batch');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error creating batch:', error);

      // Handle validation errors specifically
      if (error.response?.data?.validation_errors) {
        const validationErrors = error.response.data.validation_errors.join(', ');
        throw new Error(`Validation errors: ${validationErrors}`);
      }

      throw new Error(
        error.response?.data?.error || error.message || 'Failed to create batch'
      );
    }
  },

  /**
   * Get batch progress statistics
   */
  getBatchProgress: async (
    batchId: string,
    token: string
  ): Promise<BatchProgressResponse> => {
    try {
      const response = await apiClient.get(`/api/batches/${batchId}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch batch progress');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching batch progress:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to fetch batch progress'
      );
    }
  },

  /**
   * Get pairs of sites within a distance threshold (TECH-102). Threshold
   * resolves: query param -> BATCH_PROXIMITY_THRESHOLD_METERS env var -> 1000.
   */
  getProximityAlerts: async (
    batchId: string,
    token: string,
    thresholdMeters?: number
  ): Promise<ProximityAlertsResponse> => {
    try {
      const params = thresholdMeters !== undefined ? { threshold_meters: thresholdMeters } : undefined;
      const response = await apiClient.get(`/api/batches/${batchId}/proximity-alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch proximity alerts');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching proximity alerts:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to fetch proximity alerts'
      );
    }
  },

  /**
   * Pre-load solar API data for all locations in batch
   */
  preloadSolar: async (
    batchId: string,
    request: PreloadSolarRequest,
    token: string
  ): Promise<PreloadSolarResponse> => {
    try {
      const response = await apiClient.post(
        `/api/batches/${batchId}/preload-solar`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000, // 5 minutes for solar preload
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to preload solar data');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error preloading solar data:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to preload solar data'
      );
    }
  },

  /**
   * Update location status in batch
   */
  updateLocationStatus: async (
    batchId: string,
    dealId: string,
    status: BatchLocation['status'],
    token: string
  ): Promise<void> => {
    try {
      const response = await apiClient.patch(
        `/api/batches/${batchId}/locations/${dealId}`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update location status');
      }
    } catch (error: any) {
      console.error('Error updating location status:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to update location status'
      );
    }
  },

  /**
   * Add locations to an existing batch
   */
  addLocationsToBatch: async (
    batchId: string,
    csvContent: string,
    token: string
  ): Promise<any> => {
    try {
      const response = await apiClient.post(
        `/api/batches/${batchId}/add-locations`,
        { csv_content: csvContent },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000, // 5 minutes for large batch additions (200+ locations)
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to add locations');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error adding locations to batch:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to add locations'
      );
    }
  },

  /**
   * Delete batch
   */
  deleteBatch: async (batchId: string, token: string): Promise<void> => {
    try {
      const response = await apiClient.delete(`/api/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete batch');
      }
    } catch (error: any) {
      console.error('Error deleting batch:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to delete batch'
      );
    }
  },

  /**
   * Update the due_date (fecha compromiso) of a batch.
   * Pass `null` to clear the date. Format: ISO YYYY-MM-DD (sin hora).
   */
  updateBatchDueDate: async (
    batchId: string,
    dueDate: string | null,
    token: string
  ): Promise<Batch> => {
    try {
      const response = await apiClient.patch(
        `/api/batches/${batchId}`,
        { due_date: dueDate },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update batch due date');
      }

      return response.data.batch;
    } catch (error: any) {
      console.error('Error updating batch due date:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to update batch due date'
      );
    }
  },

  /**
   * Update the priority of a batch (TECH-79).
   * Backend handles auditoria (priority_set_at / priority_set_by) automatically.
   */
  updateBatchPriority: async (
    batchId: string,
    priority: 'alta' | 'media' | 'baja',
    token: string
  ): Promise<Batch> => {
    try {
      const response = await apiClient.patch(
        `/api/batches/${batchId}`,
        { priority },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update batch priority');
      }

      return response.data.batch;
    } catch (error: any) {
      console.error('Error updating batch priority:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to update batch priority'
      );
    }
  },

  /**
   * Check and update batch location statuses from GeoData roof_ai
   */
  checkBatchStatus: async (batchId: string, token: string): Promise<any> => {
    try {
      const response = await apiClient.get(`/api/batches/${batchId}/check-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to check batch status');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error checking batch status:', error);
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to check batch status'
      );
    }
  },

  /**
   * Update a batch location (admin only)
   */
  updateBatchLocation: async (
    batchId: string,
    rpu: string,
    updates: {
      deal_id?: string | null;
      user_name?: string | null;
      target_modules?: number | null;
      latitude?: number | null;
      longitude?: number | null;
      odd_prefactibility_module_count?: number | null;
      odd_pre_factibility_notes?: string | null;
    },
    token: string
  ): Promise<any> => {
    try {
      const response = await apiClient.patch(
        `/api/batches/${batchId}/locations`,
        { rpu, ...updates },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to update location');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error updating batch location:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to update location'
      );
    }
  },

  /**
   * Segment assign: divide batch locations among multiple users (admin only).
   *
   * Supports 3 modes: single (1 user), equal (N users, auto-divided),
   * custom (per-user percentages). Replaces the old bulk-assign-by-checkboxes
   * workflow with a full-batch assignment in one operation.
   */
  segmentAssign: async (
    batchId: string,
    request: SegmentAssignRequest,
    token: string
  ): Promise<SegmentAssignResponse> => {
    try {
      const response = await apiClient.post(
        `/api/batches/${batchId}/segment-assign`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000, // 60s for large batches with many locations to update
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to segment assign');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error in segment assign:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to segment assign locations'
      );
    }
  },

  /**
   * Bulk update multiple locations in a batch (admin only)
   * Used for bulk assigning users to multiple RPUs
   */
  bulkUpdateLocations: async (
    batchId: string,
    rpus: string[],
    updates: {
      user_name?: string | null;
    },
    token: string
  ): Promise<{ success: boolean; updated_count: number; errors: string[] }> => {
    try {
      const response = await apiClient.patch(
        `/api/batches/${batchId}/locations/bulk`,
        { rpus, ...updates },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to bulk update locations');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error bulk updating batch locations:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to bulk update locations'
      );
    }
  },

  /**
   * Parse CSV file to CSV rows
   */
  parseCSV: (file: File): Promise<CSVRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim());

          if (lines.length < 2) {
            throw new Error('CSV must have at least a header row and one data row');
          }

          // Parse header
          const headers = lines[0].split(',').map((h) => h.trim());

          // Validate required headers
          if (!headers.includes('rpu')) {
            throw new Error(
              'CSV must have "rpu" column (required). Optional: "deal_id", "user_name", "coordinates" (or "lat"/"long"), "target_modules", "odd_prefactibility_module_count", "odd_pre_factibility_notes"'
            );
          }

          // Parse rows
          const rows: CSVRow[] = lines.slice(1).map((line) => {
            // Handle quoted values (for coordinates like "19.4326,-99.1332")
            const values: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim());

            const row: any = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });

            // Zero-pad RPU to 12 digits if it's a number
            let rpu = row.rpu || row.RPU || undefined;
            if (rpu) {
              const rpuDigits = rpu.replace(/\D/g, ''); // Remove non-digits
              if (rpuDigits) {
                rpu = rpuDigits.padStart(12, '0');
              }
            }

            return {
              deal_id: row.deal_id,
              rpu: rpu,
              lat: row.lat || undefined,
              long: row.long || undefined,
              coordinates: row.coordinates || undefined,
              user_name: row.user_name || row.user || undefined,
              target_modules: row.target_modules || undefined,
              odd_prefactibility_module_count: row.odd_prefactibility_module_count || undefined,
              odd_pre_factibility_notes: row.odd_pre_factibility_notes || undefined,
            };
          });

          resolve(rows);
        } catch (error: any) {
          reject(new Error(`CSV parsing error: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };

      reader.readAsText(file);
    });
  },

  /**
   * Validate and auto-correct deal_ids for CSV rows
   *
   * Returns validation results with warnings and corrections
   */
  validateDealIds: async (
    rows: CSVRow[]
  ): Promise<{
    rows: CSVRow[];
    warnings: string[];
    corrections: { rpu: string; old_deal_id: string; new_deal_id: string }[];
    auto_populated: { rpu: string; deal_id: string }[];
    errors: string[];
  }> => {
    const warnings: string[] = [];
    const corrections: { rpu: string; old_deal_id: string; new_deal_id: string }[] = [];
    const auto_populated: { rpu: string; deal_id: string }[] = [];
    const errors: string[] = [];
    const validatedRows: CSVRow[] = [];

    for (const row of rows) {
      const validatedRow = { ...row };

      // Skip if no RPU
      if (!row.rpu) {
        errors.push('Row has no RPU - skipping validation');
        validatedRows.push(validatedRow);
        continue;
      }

      // Check for multiple deal_ids for this RPU
      try {
        const multipleCheck = await checkForMultipleDealIds(row.rpu);
        if (multipleCheck.has_multiple) {
          errors.push(
            `RPU ${row.rpu} has multiple deal_ids: ${multipleCheck.deal_ids.join(', ')}`
          );
        }
      } catch (err: any) {
        console.warn(`Failed to check multiple deal_ids for RPU ${row.rpu}:`, err.message);
      }

      // If no deal_id provided, search by RPU
      if (!row.deal_id || !row.deal_id.trim()) {
        try {
          const deal = await findDealByRPU(row.rpu);
          if (deal && deal.deal_id) {
            validatedRow.deal_id = deal.deal_id;
            auto_populated.push({
              rpu: row.rpu,
              deal_id: deal.deal_id,
            });
            warnings.push(`RPU ${row.rpu}: Auto-populated deal_id ${deal.deal_id}`);
          }
        } catch (err: any) {
          console.warn(`Failed to search deal by RPU ${row.rpu}:`, err.message);
        }
      } else {
        // Validate and correct deal_id if provided
        try {
          const validation = await validateAndCorrectDealId(row.deal_id, row.rpu);

          if (!validation.valid && validation.corrected_deal_id) {
            // Auto-correct mock deal_ids
            corrections.push({
              rpu: row.rpu,
              old_deal_id: row.deal_id,
              new_deal_id: validation.corrected_deal_id,
            });
            validatedRow.deal_id = validation.corrected_deal_id;
            warnings.push(`RPU ${row.rpu}: ${validation.message}`);
          } else if (!validation.valid && validation.suggestion) {
            // Suggest correction
            warnings.push(
              `RPU ${row.rpu}: ${validation.message} (Suggested: ${validation.suggestion})`
            );
          } else if (!validation.valid) {
            // Flag error
            errors.push(`RPU ${row.rpu}: ${validation.message}`);
          }
        } catch (err: any) {
          console.warn(`Failed to validate deal_id for RPU ${row.rpu}:`, err.message);
        }
      }

      validatedRows.push(validatedRow);
    }

    return {
      rows: validatedRows,
      warnings,
      corrections,
      auto_populated,
      errors,
    };
  },

  /**
   * Convert CSV rows to CSV string
   */
  rowsToCSV: (rows: CSVRow[]): string => {
    if (rows.length === 0) {
      return 'deal_id,lat,long,rpu,user\n';
    }

    // Header
    const header = 'deal_id,lat,long,rpu,user';

    // Rows
    const rowStrings = rows.map((row) => {
      return [
        row.deal_id,
        row.lat || '',
        row.long || '',
        row.rpu,
        row.user_name || '',
      ].join(',');
    });

    return [header, ...rowStrings].join('\n');
  },

  /**
   * Delete a location from a batch
   */
  deleteBatchLocation: async (
    batchId: string,
    rpu: string,
    token: string
  ): Promise<{ success: boolean; remaining_locations: number }> => {
    try {
      const response = await apiClient.delete(
        `/api/batches/${batchId}/locations/${rpu}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete location');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error deleting batch location:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Failed to delete location'
      );
    }
  },

  /**
   * Validate single deal_id by RPU
   * Looks up the RPU in GeoData and returns the deal_id if found
   */
  validateSingleDealId: async (
    rpu: string
  ): Promise<{ found: boolean; deal_id: string | null }> => {
    try {
      // Query the RPU endpoint to get deal_id from GeoData
      const response = await apiClient.get(`/api/rpu/${rpu}`);

      if (response.data && response.data.deal_id) {
        return {
          found: true,
          deal_id: response.data.deal_id,
        };
      }

      return {
        found: false,
        deal_id: null,
      };
    } catch (error: any) {
      console.warn(`Failed to validate deal_id for RPU ${rpu}:`, error.message);
      return {
        found: false,
        deal_id: null,
      };
    }
  },

  /**
   * Download CSV template
   */
  downloadCSVTemplate: (): void => {
    const template = `rpu,deal_id,user_name,coordinates,target_modules,odd_prefactibility_module_count,odd_pre_factibility_notes
571910401294,DEAL-001,andy@niko.mx,"19.4326,-99.1332",12,10,Pre-factibility notes go here
123456,DEAL-002,juani@niko.mx,,15,,
571910401296,DEAL-003,,"19.4328,-99.1334",,,`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Sync assigned_users array for all batches (admin only)
   * This ensures each batch's assigned_users array matches its locations' assigned_user fields
   */
  syncAssignedUsers: async (token: string): Promise<{ total_batches: number; updated_batches: number }> => {
    try {
      const response = await apiClient.post(
        '/api/batches/sync-users',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        total_batches: response.data.total_batches,
        updated_batches: response.data.updated_batches,
      };
    } catch (error) {
      console.error('Failed to sync assigned users:', error);
      throw error;
    }
  },
};
